import { queryOptions, useQuery } from '@tanstack/vue-query';
import type { ApiPaginatedResponse, ApiProductPage,
  ApiProductHeadingBlock,
  ApiProductDescriptionBlock,
  ApiProductShortDescriptionBlock,
  ApiProductIconBlock,
  ApiProductPriceBlock,
  ApiProductTermsBlock,
  ApiProductFeaturesBlock,
  ApiProductRelatedProductsBlock,
  ApiProductContentBlock } from '@/types';
import { getBlock } from '@/utils/getBlock';

function prepareProduct(product: ApiProductPage) {
  return {
    product,
    heading: getBlock<ApiProductHeadingBlock>(product.body, 'heading'),
    description: getBlock<ApiProductDescriptionBlock>(product.body, 'description'),
    shortDescription: getBlock<ApiProductShortDescriptionBlock>(product.body, 'shortDescription'),
    icon: getBlock<ApiProductIconBlock>(product.body, 'icon'),
    price: getBlock<ApiProductPriceBlock>(product.body, 'price'),
    terms: getBlock<ApiProductTermsBlock>(product.body, 'terms'),
    features: getBlock<ApiProductFeaturesBlock>(product.body, 'features'),
    relatedProducts: getBlock<ApiProductRelatedProductsBlock>(product.body, 'relatedProducts'),
    content: getBlock<ApiProductContentBlock>(product.body, 'content'),
  };
}

export function productsQueryOptions(options: { ids?: number[]; slugs?: string[] } = {}) {
  const { cmsFetch } = useCms();
  return queryOptions({
    queryKey: ['cms', 'pages', 'products', { ids: options.ids, slugs: options.slugs }] as const,
    queryFn: () => cmsFetch<ApiPaginatedResponse<ApiProductPage>>('/api/pages/', {
      type: 'pages.ProductPage',
      fields: '*',
      // Wagtail id filter only accepts a single value; skip it for multi-ID queries
      id: options.ids?.length === 1 ? options.ids[0] : undefined,
      slug: options.slugs,
    }),
  });
}

export function useProducts(options: { ids?: MaybeRef<number[]>; slugs?: MaybeRef<string[]> } = {}) {
  const query = useQuery(computed(() =>
    productsQueryOptions({ ids: toValue(options.ids), slugs: toValue(options.slugs) }),
  ));

  function getById(id: number) {
    const product = query.data.value?.items?.find(p => p.id === id);
    return product ? prepareProduct(product) : undefined;
  }

  function getBySlug(slug: string) {
    const product = query.data.value?.items?.find(p => p.meta.slug === slug);
    return product ? prepareProduct(product) : undefined;
  }

  return { ...query, getById, getBySlug };
}
